import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import { useLanguage } from "../lib/language";

export const Datenschutz = () => {
  const { t, lang } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isDE = lang === "de";
  const sections = t.datenschutz.sections;

  return (
    <main
      data-testid="datenschutz-page"
      className="bg-resilie-bg text-resilie-ink min-h-screen"
    >
      <Header forceLight={true} />

      <article className="px-6 md:px-12 pt-32 md:pt-40 pb-24 md:pb-32">
        <div className="max-w-3xl mx-auto">
          {/* Back link */}
          <Link
            to="/"
            data-testid="datenschutz-back-link"
            className="nav-link inline-flex items-center gap-2 text-resilie-muted hover:text-resilie-ink"
          >
            ← {isDE ? "Zurück" : "Back"}
          </Link>

          {/* Kicker */}
          <p
            data-testid="datenschutz-kicker"
            className="mt-12 font-sans text-[10px] md:text-[11px] tracking-[0.5em] uppercase text-resilie-muted"
          >
            — {isDE ? "Rechtliches" : "Legal"} —
          </p>

          {/* Title */}
          <h1
            data-testid="datenschutz-title"
            className="mt-6 font-serif font-light leading-[1.05] tracking-tight text-resilie-ink
                       text-4xl sm:text-5xl md:text-6xl"
          >
            {t.datenschutz.title}
          </h1>

          <p
            data-testid="datenschutz-updated"
            className="mt-6 font-sans text-[10px] tracking-[0.32em] uppercase text-resilie-muted"
          >
            {t.datenschutz.updated_label}: {t.datenschutz.updated_date}
          </p>

          <hr className="mt-12 border-resilie-line" />

          {/* Intro */}
          <p
            data-testid="datenschutz-intro"
            className="mt-12 font-serif font-light text-[22px] md:text-[26px] leading-[1.45] text-resilie-ink/90"
          >
            {t.datenschutz.intro}
          </p>

          {/* Sections */}
          <div className="mt-16 space-y-14 font-sans text-[15px] leading-[1.85] text-resilie-ink/85">

            {/* Controller */}
            <section data-testid="ds-controller">
              <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-resilie-muted mb-4">
                {sections.controller.kicker}
              </p>
              <h2 className="font-serif text-[28px] md:text-[34px] font-light leading-[1.2] text-resilie-ink mb-6">
                {sections.controller.heading}
              </h2>
              <p>
                Maison Resilié GmbH<br />
                Wellekamp 21<br />
                38440 Wolfsburg, {isDE ? "Deutschland" : "Germany"}<br />
                {isDE ? "E-Mail" : "Email"}:{" "}
                <a href="mailto:info@resilie.de" className="underline-offset-4 hover:underline">
                  info@resilie.de
                </a>
              </p>
            </section>

            {/* General */}
            <section data-testid="ds-general">
              <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-resilie-muted mb-4">
                {sections.general.kicker}
              </p>
              <h2 className="font-serif text-[28px] md:text-[34px] font-light leading-[1.2] text-resilie-ink mb-6">
                {sections.general.heading}
              </h2>
              <p>{sections.general.body_1}</p>
              <p className="mt-4">{sections.general.body_2}</p>
            </section>

            {/* Hosting */}
            <section data-testid="ds-hosting">
              <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-resilie-muted mb-4">
                {sections.hosting.kicker}
              </p>
              <h2 className="font-serif text-[28px] md:text-[34px] font-light leading-[1.2] text-resilie-ink mb-6">
                {sections.hosting.heading}
              </h2>
              <p>{sections.hosting.body}</p>
              <p className="mt-4">{sections.hosting.legal_basis}</p>
            </section>

            {/* Server logs */}
            <section data-testid="ds-logs">
              <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-resilie-muted mb-4">
                {sections.logs.kicker}
              </p>
              <h2 className="font-serif text-[28px] md:text-[34px] font-light leading-[1.2] text-resilie-ink mb-6">
                {sections.logs.heading}
              </h2>
              <p>{sections.logs.body}</p>
              <ul className="mt-4 space-y-2 list-disc pl-5">
                {sections.logs.list.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              <p className="mt-4">{sections.logs.retention}</p>
            </section>

            {/* Cookies */}
            <section data-testid="ds-cookies">
              <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-resilie-muted mb-4">
                {sections.cookies.kicker}
              </p>
              <h2 className="font-serif text-[28px] md:text-[34px] font-light leading-[1.2] text-resilie-ink mb-6">
                {sections.cookies.heading}
              </h2>
              <p>{sections.cookies.body_1}</p>
              <p className="mt-4">{sections.cookies.body_2}</p>
            </section>

            {/* Newsletter */}
            <section data-testid="ds-newsletter">
              <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-resilie-muted mb-4">
                {sections.newsletter.kicker}
              </p>
              <h2 className="font-serif text-[28px] md:text-[34px] font-light leading-[1.2] text-resilie-ink mb-6">
                {sections.newsletter.heading}
              </h2>
              <p>{sections.newsletter.body_1}</p>
              <p className="mt-4">{sections.newsletter.body_2}</p>
              <p className="mt-4">{sections.newsletter.body_3}</p>
              <p className="mt-4">
                {sections.newsletter.processor}{" "}
                <a
                  href="https://resend.com/legal/privacy-policy"
                  target="_blank"
                  rel="noreferrer"
                  className="underline-offset-4 hover:underline"
                >
                  resend.com/legal/privacy-policy
                </a>
                .
              </p>
            </section>

            {/* Contact */}
            <section data-testid="ds-contact">
              <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-resilie-muted mb-4">
                {sections.contact.kicker}
              </p>
              <h2 className="font-serif text-[28px] md:text-[34px] font-light leading-[1.2] text-resilie-ink mb-6">
                {sections.contact.heading}
              </h2>
              <p>{sections.contact.body}</p>
            </section>

            {/* Analytics */}
            <section data-testid="ds-analytics">
              <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-resilie-muted mb-4">
                {sections.analytics.kicker}
              </p>
              <h2 className="font-serif text-[28px] md:text-[34px] font-light leading-[1.2] text-resilie-ink mb-6">
                {sections.analytics.heading}
              </h2>
              <p>{sections.analytics.body}</p>
            </section>

            {/* Rights */}
            <section data-testid="ds-rights">
              <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-resilie-muted mb-4">
                {sections.rights.kicker}
              </p>
              <h2 className="font-serif text-[28px] md:text-[34px] font-light leading-[1.2] text-resilie-ink mb-6">
                {sections.rights.heading}
              </h2>
              <p>{sections.rights.intro}</p>
              <ul className="mt-4 space-y-2 list-disc pl-5">
                {sections.rights.list.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              <p className="mt-4">{sections.rights.contact}</p>
              <p className="mt-4">{sections.rights.complaint}</p>
            </section>

            {/* Storage duration */}
            <section data-testid="ds-storage">
              <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-resilie-muted mb-4">
                {sections.storage.kicker}
              </p>
              <h2 className="font-serif text-[28px] md:text-[34px] font-light leading-[1.2] text-resilie-ink mb-6">
                {sections.storage.heading}
              </h2>
              <p>{sections.storage.body}</p>
            </section>

            {/* Changes */}
            <section data-testid="ds-changes">
              <p className="font-sans text-[10px] tracking-[0.4em] uppercase text-resilie-muted mb-4">
                {sections.changes.kicker}
              </p>
              <h2 className="font-serif text-[28px] md:text-[34px] font-light leading-[1.2] text-resilie-ink mb-6">
                {sections.changes.heading}
              </h2>
              <p>{sections.changes.body}</p>
            </section>

          </div>
        </div>
      </article>

      <Footer />
    </main>
  );
};

export default Datenschutz;
